import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Users, 
  Plus, 
  Mail, 
  Clock,
  CheckCircle,
  XCircle,
  UserPlus
} from "lucide-react";
import { toast } from "sonner";

export const TeamPage = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [invitations, setInvitations] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    role: "worker"
  });

  const roles = [
    { value: "worker", label: language === "sv" ? "Yrkesarbetare" : "Worker" },
    { value: "supervisor", label: language === "sv" ? "Arbetsledare" : "Supervisor" },
    { value: "site_manager", label: language === "sv" ? "Platschef" : "Site Manager" },
    { value: "project_manager", label: language === "sv" ? "Projektledare" : "Project Manager" },
    { value: "admin", label: language === "sv" ? "Admin" : "Admin" }
  ];

  useEffect(() => {
    if (user?.organization_id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [invRes, usersRes] = await Promise.all([
        fetch(`${API}/organizations/${user.organization_id}/invitations`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        }),
        fetch(`${API}/organizations/${user.organization_id}/users`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        })
      ]);
      
      if (invRes.ok) {
        setInvitations(await invRes.json());
      }
      if (usersRes.ok) {
        setOrgUsers(await usersRes.json());
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.organization_id) return;

    try {
      const res = await fetch(`${API}/organizations/${user.organization_id}/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(language === "sv" ? "Inbjudan skickad!" : "Invitation sent!");
        setDialogOpen(false);
        setFormData({ email: "", role: "worker" });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || (language === "sv" ? "Kunde inte skicka inbjudan" : "Failed to send invitation"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const statusConfig = {
    pending: { color: "bg-amber-100 text-amber-800", icon: Clock, label: language === "sv" ? "Väntande" : "Pending" },
    accepted: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: language === "sv" ? "Accepterad" : "Accepted" },
    expired: { color: "bg-slate-100 text-slate-800", icon: XCircle, label: language === "sv" ? "Utgången" : "Expired" }
  };

  const canInvite = user?.role === "admin" || user?.role === "superadmin";

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Team & Användare" : "Team & Users"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? "Bjud in teammedlemmar till din organisation" 
                : "Invite team members to your organization"}
            </p>
          </div>

          {canInvite && user?.organization_id && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-touch" data-testid="invite-btn">
                  <UserPlus className="w-5 h-5 mr-2" />
                  {language === "sv" ? "Bjud in" : "Invite"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                    {language === "sv" ? "Bjud in teammedlem" : "Invite Team Member"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "E-postadress" : "Email address"}</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="kollega@foretag.se"
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Roll" : "Role"}</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === "sv" 
                      ? "Personen får en inbjudan som är giltig i 7 dagar." 
                      : "The person will receive an invitation valid for 7 days."}
                  </p>
                  <Button type="submit" className="w-full" data-testid="invite-submit-btn">
                    {language === "sv" ? "Skicka inbjudan" : "Send invitation"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* No Organization */}
        {!user?.organization_id && (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">
                {language === "sv" 
                  ? "Du är inte ansluten till någon organisation" 
                  : "You are not connected to any organization"}
              </p>
              <p className="text-sm text-slate-500">
                {language === "sv" 
                  ? "Gå till Inställningar för att skapa eller gå med i en organisation" 
                  : "Go to Settings to create or join an organization"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Current User Info */}
        {user?.organization_id && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Din profil" : "Your Profile"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <Badge className="mt-1 capitalize">{roles.find(r => r.value === user?.role)?.label || user?.role}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Organization Users */}
        {user?.organization_id && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Teammedlemmar" : "Team Members"}
              </h2>
              <Badge variant="outline">{orgUsers.length} {language === "sv" ? "användare" : "users"}</Badge>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : orgUsers.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  {language === "sv" ? "Inga teammedlemmar ännu" : "No team members yet"}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {orgUsers.map((orgUser) => {
                  const role = roles.find(r => r.value === orgUser.role);
                  const isCurrentUser = orgUser.user_id === user?.user_id;
                  
                  return (
                    <Card key={orgUser.user_id} className={isCurrentUser ? "border-primary" : ""} data-testid={`team-member-${orgUser.user_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${isCurrentUser ? 'bg-primary' : 'bg-slate-400'}`}>
                            {orgUser.name?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {orgUser.name}
                              {isCurrentUser && <span className="text-primary text-xs ml-1">({language === "sv" ? "du" : "you"})</span>}
                            </p>
                            <p className="text-sm text-slate-500 truncate">{orgUser.email}</p>
                            <Badge className="mt-1 text-xs capitalize">{role?.label || orgUser.role}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Invitations List */}
        {user?.organization_id && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Inbjudningar" : "Invitations"}
              </h2>
              <Badge variant="outline">{invitations.length} {language === "sv" ? "totalt" : "total"}</Badge>
            </div>

            {invitations.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-slate-500">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  {language === "sv" ? "Inga inbjudningar skickade ännu" : "No invitations sent yet"}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {invitations.map((invitation) => {
                  const status = statusConfig[invitation.status] || statusConfig.pending;
                  const role = roles.find(r => r.value === invitation.role);
                  
                  return (
                    <Card key={invitation.invitation_id} data-testid={`invitation-${invitation.invitation_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Mail className="w-5 h-5 text-slate-400" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{invitation.email}</p>
                              <p className="text-sm text-slate-500">{role?.label || invitation.role}</p>
                            </div>
                          </div>
                          <Badge className={status.color}>
                            <status.icon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Help Text */}
        {user?.organization_id && canInvite && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-blue-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Tips" : "Tips"}
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {language === "sv" ? "Inbjudningar är giltiga i 7 dagar" : "Invitations are valid for 7 days"}</li>
                <li>• {language === "sv" ? "Mottagaren måste registrera sig med samma e-postadress" : "Recipients must register with the same email"}</li>
                <li>• {language === "sv" ? "Du kan bjuda in flera personer samtidigt" : "You can invite multiple people"}</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};
